import React, { useEffect, useId, useMemo, useState } from "react";
import {
  RegistrationFormData,
  validateRegistrationForm,
  getResponseOptions,
  getMealPreferenceOptions,
} from "../../types/registration.types";
import { registerForEvent } from "../../hooks/useRegistration";
import Button from "../shared/Button";
import FormField from "../shared/FormField";
import "./RegistrationForm.css";
import { useNotification } from "../../context/NotificationContext";
import {
  getStatusClass,
  getStatusIcon,
  getSubmitButtonText,
} from "../utils/statusUtils";
import { EventRegistration, MealPreference, RegistrationStatus } from "../../types/shared.types";

interface RegistrationFormProps {
  eventId: string;
  mealOptionsEnabled: boolean;
  additionalGuestsEnabled: boolean;
  onSuccess: (registration: EventRegistration) => void;
  onCancel?: () => void;
  initialEmail?: string;
  initialData?: Partial<RegistrationFormData>;
  isUpdateMode?: boolean;
}

const RegistrationForm: React.FC<RegistrationFormProps> = ({
  eventId,
  mealOptionsEnabled,
  additionalGuestsEnabled,
  onSuccess,
  onCancel,
  initialEmail = "",
  initialData,
  isUpdateMode = false,
}) => {
  const formId = useId();

  const [formData, setFormData] = useState<RegistrationFormData>({
    userName: initialData?.userName || "",
    userEmail: initialData?.userEmail || initialEmail,
    employeeId: initialData?.employeeId || "",
    sector: initialData?.sector || "",
    cluster: initialData?.cluster || "",
    mealPreference: initialData?.mealPreference || MealPreference.None,
    status: initialData?.status || RegistrationStatus.Registered,
    notes: initialData?.notes || "",
    additionalAdults: initialData?.additionalAdults ?? 0,
    additionalChildren: initialData?.additionalChildren ?? 0,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { setNotification } = useNotification();

  useEffect(() => {
    const hasEmailAlready = !!formData.userEmail?.trim();
    const hasNameAlready = !!formData.userName?.trim();

    const storedEmail = sessionStorage.getItem("userEmail")?.trim();
    const storedName = sessionStorage.getItem("userName")?.trim();

    setFormData((prev) => ({
      ...prev,
      userEmail: hasEmailAlready
        ? prev.userEmail
        : storedEmail
          ? storedEmail.toLowerCase()
          : prev.userEmail,
      userName: hasNameAlready ? prev.userName : storedName || prev.userName,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const responseOptions = useMemo(() => getResponseOptions(), []);
  const mealOptions = useMemo(() => getMealPreferenceOptions(), []);

  const handleTextChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? (parseInt(value, 10) || 0) : value,
    }));
  };

  const handleResponseSelect = (status: RegistrationStatus) => {
    setFormData((prev) => ({
      ...prev,
      status,
      mealPreference:
        status === RegistrationStatus.Declined
          ? MealPreference.None
          : prev.mealPreference,
    }));
  };

  const handleMealSelect = (preference: MealPreference) => {
    setFormData((prev) => ({ ...prev, mealPreference: preference }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateRegistrationForm(
      formData,
      mealOptionsEnabled,
    );
    if (validationErrors.length > 0) {
      setNotification({
        type: "error",
        message: validationErrors.join(". "),
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await registerForEvent(
        eventId,
        {
          userName: formData.userName.trim(),
          userEmail: formData.userEmail.trim().toLowerCase(),
          employeeId: formData.employeeId?.trim() || undefined,
          sector: formData.sector?.trim() || undefined,
          cluster: formData.cluster?.trim() || undefined,
          mealPreference: formData.mealPreference,
          status: formData.status,
          notes: formData.notes?.trim(),
          additionalAdults: formData.additionalAdults ?? 0,
          additionalChildren: formData.additionalChildren ?? 0,
        },
        isUpdateMode,
      );

      if (response.success && response.data) {
        onSuccess(response.data);
      } else {
        setNotification({
          type: "error",
          message: response.error || "Registration failed. Please try again.",
        });
      }
    } catch {
      setNotification({
        type: "error",
        message: "An unexpected error occurred. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const showMealOptions =
    mealOptionsEnabled && formData.status === RegistrationStatus.Registered;

  const showAdditionalGuests =
    additionalGuestsEnabled && formData.status === RegistrationStatus.Registered;

  return (
    <form
      onSubmit={handleSubmit}
      className="cgi-regform"
      aria-busy={isSubmitting}
    >
      <header className="cgi-regform__header">
        <h2 className="cgi-regform__title">
          {isUpdateMode ? "Update Your Response" : "Register for this Event"}
        </h2>
        <p className="cgi-regform__subtitle">
          {isUpdateMode
            ? "Update your details and response below."
            : "Please confirm your details and select your response."}
        </p>
      </header>

      <section
        className="cgi-regform__section"
        aria-labelledby={`${formId}-info`}
      >
        <h3 id={`${formId}-info`} className="cgi-regform__sectionTitle">
          Your Information
        </h3>

        <FormField
          label="Full Name"
          id={`${formId}-userName`}
          name="userName"
          type="text"
          value={formData.userName}
          onChange={handleTextChange}
          placeholder="Enter your full name"
          required
          maxLength={100}
          disabled={isSubmitting}
          autoComplete="name"
        />
        <FormField
          label="Employee ID"
          id={`${formId}-employeeId`}
          name="employeeId"
          type="text"
          value={formData.employeeId || ""}
          onChange={handleTextChange}
          maxLength={50}
          disabled={isSubmitting}
        />

        <FormField
          label="Sector"
          id={`${formId}-sector`}
          name="sector"
          type="text"
          value={formData.sector || ""}
          onChange={handleTextChange}
          maxLength={100}
          disabled={isSubmitting}
        />

        <FormField
          label="Cluster"
          id={`${formId}-cluster`}
          name="cluster"
          type="text"
          value={formData.cluster || ""}
          onChange={handleTextChange}
          maxLength={100}
          disabled={isSubmitting}
        />

        <FormField
          label="Additional Notes (Optional)"
          id={`${formId}-notesField`}
          name="notes"
          multiline
          value={formData.notes || ""}
          onChange={handleTextChange}
          placeholder="Dietary restrictions, accessibility needs, or other information..."
          rows={3}
          maxLength={300}
          disabled={isSubmitting}
        />

        <p className="cgi-regform__hint">
          {isUpdateMode
            ? "Your registration will be updated after submitting."
            : "You'll see your confirmation (QR code) after submitting."}
        </p>
      </section>

      <section
        className="cgi-regform__section"
        aria-labelledby={`${formId}-response`}
      >
        <h3 id={`${formId}-response`} className="cgi-regform__sectionTitle">
          Your Response
        </h3>

        <div
          className="cgi-regform__segmented"
          role="radiogroup"
          aria-label="Registration response"
        >
          {responseOptions.map((option) => {
            const isSelected = formData.status === option.value;
            const statusClass = getStatusClass(option.value);

            return (
              <button
                key={option.value}
                type="button"
                className={`custom-btn cgi-regform__segBtn ${statusClass} ${
                  isSelected ? "is-selected" : ""
                }`}
                onClick={() => handleResponseSelect(option.value)}
                disabled={isSubmitting}
                role="radio"
                aria-checked={isSelected}
              >
                <span className="cgi-regform__segIcon" aria-hidden="true">
                  {getStatusIcon(option.value)}
                </span>
                <span className="cgi-regform__segText">
                  <span className="cgi-regform__segLabel">{option.label}</span>
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {showMealOptions && (
        <section
          className="cgi-regform__section"
          aria-labelledby={`${formId}-meal`}
        >
          <h3 id={`${formId}-meal`} className="cgi-regform__sectionTitle">
            Meal Preference
          </h3>

          <div
            className="cgi-regform__segmented cgi-regform__segmented--2col"
            role="radiogroup"
            aria-label="Meal preference"
          >
            {mealOptions.map((option) => {
              const isSelected = formData.mealPreference === option.value;

              return (
                <button
                  key={option.value}
                  type="button"
                  className={`custom-btn cgi-regform__segBtn${option.value === "NoMeal" ? " cgi-regform__segBtn--full" : ""} ${isSelected ? "is-selected" : ""}`}
                  onClick={() => handleMealSelect(option.value)}
                  disabled={isSubmitting}
                  role="radio"
                  aria-checked={isSelected}
                >
                  <span className="cgi-regform__segLabel">{option.label}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {showAdditionalGuests && (
        <section
          className="cgi-regform__section"
          aria-labelledby={`${formId}-guests`}
        >
          <h3 id={`${formId}-guests`} className="cgi-regform__sectionTitle">
            Additional Guests
          </h3>
          <p className="cgi-regform__hint">
            How many additional guests will you bring? (No name or meal info required for guests)
          </p>

          <FormField
            label="Additional Adults"
            id={`${formId}-additionalAdults`}
            name="additionalAdults"
            type="number"
            value={(formData.additionalAdults ?? 0).toString()}
            onChange={handleTextChange}
            min="0"
            max="20"
            disabled={isSubmitting}
          />

          <FormField
            label="Additional Children"
            id={`${formId}-additionalChildren`}
            name="additionalChildren"
            type="number"
            value={(formData.additionalChildren ?? 0).toString()}
            onChange={handleTextChange}
            min="0"
            max="20"
            disabled={isSubmitting}
          />
        </section>
      )}

      <footer className="cgi-regform__actions">
        {onCancel && (
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        )}

        <Button
          type="submit"
          variant={
            formData.status === RegistrationStatus.Declined
              ? "secondary"
              : "primary"
          }
          disabled={isSubmitting}
        >
          {getSubmitButtonText(isSubmitting, formData.status, isUpdateMode)}
        </Button>
      </footer>
    </form>
  );
};

export default RegistrationForm;
